import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Title1,
    Title3,
    Body1,
    Body2,
    Caption1,
    Button,
    MessageBar,
    MessageBarBody,
    Spinner,
    Badge,
    Dropdown,
    Option,
    Label,
} from '@fluentui/react-components';
import {
    ArrowUpload24Regular,
    SignOut24Regular,
    Document24Regular,
    Checkmark24Regular,
    Dismiss24Regular,
    Phone24Regular,
    ArrowDownload24Regular,
    History24Regular,
    CheckmarkCircle24Regular,
    Settings24Regular,
    Building24Regular,
    DocumentBulletList24Regular,
    Organization24Regular,
} from '@fluentui/react-icons';
import { User, uploadFile, listFiles, FileInfo, listAuditLogs, AuditEntry, getDownloadUrl, ClientInfo, listPartners, PartnerInfo } from '../services/api';
import EDIPartnersLogo from '../components/EDIPartnersLogo';
import { buildPartnerOptions, resolveAccessList } from '../utils/partnerOptions';

// Document types for EDI uploads (master list)
const ALL_DOCUMENT_TYPES = [
    { value: '810', label: '810 - Invoice' },
    { value: '850', label: '850 - Purchase Order' },
    { value: '856', label: '856 - Ship Notice' },
    { value: '997', label: '997 - Acknowledgment' },
];

// Trading partners (master list)
const ALL_TRADING_PARTNERS = [
    { value: 'bomgaars', label: 'Bomgaars' },
    { value: 'lowes', label: "Lowe's Companies" },
    { value: 'rural-king', label: 'Rural King' },
    { value: 'true-value', label: 'True Value Company' },
    { value: 'murdochs', label: "Murdoch's Ranch & Home" },
    { value: 'anda', label: 'ANDA' },
    { value: 'dollar-general', label: 'Dollar General' },
    { value: 'murphy', label: 'Murphy USA' },
    { value: 'walgreens', label: 'Walgreens' },
    { value: 'cvs', label: 'CVS' },
    { value: 'mckesson', label: 'McKesson' },
    { value: 'cardinal', label: 'Cardinal Health' },
    { value: 'amazonrx', label: 'Amazon Pharmacy' },
    { value: 'walmart', label: 'Walmart' },
];

interface UploadPageProps {
    user: User;
    onLogout: () => void;
    isAdmin?: boolean;
    clients?: ClientInfo[];
    selectedClientId?: string | null;
    onClientChange?: (clientId: string) => void;
}

export default function UploadPage({
    user,
    onLogout,
    isAdmin = false,
    clients = [],
    selectedClientId,
    onClientChange
}: UploadPageProps) {
    const navigate = useNavigate();
    const [pickedUpFiles, setPickedUpFiles] = useState<FileInfo[]>([]);
    const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [downloading, setDownloading] = useState<string | null>(null);
    const [partnerCatalog, setPartnerCatalog] = useState<PartnerInfo[]>([]);

    // Upload form state
    const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
    const [selectedPartner, setSelectedPartner] = useState<string | null>(null);

    // For admins, only use clientId when they've selected a client
    // For regular users, always use their assigned clientId
    const activeClientId = isAdmin ? selectedClientId : user.clientId;

    // Admin needs to select a client to view files
    const needsClientSelection = isAdmin && !selectedClientId;

    // Get the active client's configuration (for admins who loaded clients list)
    const activeClient = useMemo(() => {
        if (!activeClientId || !clients.length) return null;
        return clients.find(c => c.clientId.toLowerCase() === activeClientId.toLowerCase());
    }, [activeClientId, clients]);

    // Filter document types based on client's allowedDocTypes
    // For regular users, use user.allowedDocTypes from /me endpoint
    // For admins viewing a client, use activeClient.allowedDocTypes
    const allowedDocTypes = useMemo(() => {
        const allowed = isAdmin
            ? resolveAccessList(activeClient?.allowedDocTypes)
            : resolveAccessList(user.portalAccess?.documentTypes, user.portalAccess?.allowedDocTypes, user.allowedDocTypes);
        if (!allowed) return ALL_DOCUMENT_TYPES;
        return ALL_DOCUMENT_TYPES.filter(dt => allowed.includes(dt.value.toLowerCase()));
    }, [isAdmin, activeClient, user.portalAccess?.documentTypes, user.portalAccess?.allowedDocTypes, user.allowedDocTypes]);

    // Filter trading partners based on client's allowedPartners
    // For regular users, use user.allowedPartners from /me endpoint
    // For admins viewing a client, use activeClient.allowedPartners
    const allowedPartners = useMemo(() => {
        const accessValues = isAdmin
            ? [activeClient?.allowedPartners]
            : [user.portalAccess?.partners, user.portalAccess?.allowedPartners, user.allowedPartners];

        return buildPartnerOptions({
            catalogPartners: partnerCatalog,
            accessValues,
            fallbackPartners: ALL_TRADING_PARTNERS,
        });
    }, [isAdmin, activeClient, user.portalAccess?.partners, user.portalAccess?.allowedPartners, user.allowedPartners, partnerCatalog]);

    // Reset selections when client changes
    useEffect(() => {
        setSelectedDocType(null);
        setSelectedPartner(null);
    }, [activeClientId]);

    useEffect(() => {
        let cancelled = false;

        async function loadPartnerCatalog() {
            try {
                const partners = await listPartners(isAdmin && activeClientId ? activeClientId : undefined);
                if (!cancelled) {
                    setPartnerCatalog(partners);
                }
            } catch (err) {
                console.error('Failed to load partner catalog:', err);
                if (!cancelled) {
                    setPartnerCatalog([]);
                }
            }
        }

        if (!needsClientSelection) {
            loadPartnerCatalog();
        } else {
            setPartnerCatalog([]);
        }

        return () => {
            cancelled = true;
        };
    }, [activeClientId, isAdmin, needsClientSelection]);

    // Check if upload is ready
    const uploadReady = selectedDocType && selectedPartner;

    const loadAllData = useCallback(async () => {
        // Don't load data if admin hasn't selected a client
        if (needsClientSelection) {
            setPickedUpFiles([]);
            setAuditLogs([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Pass clientId for admins who have selected a client
            const clientParam = isAdmin && activeClientId ? activeClientId : undefined;
            const [pickedUp, logs] = await Promise.all([
                listFiles('outbound-dropoff-backup', clientParam).catch(() => []),
                // Only fetch UPLOAD and DOWNLOAD actions for Activity Log
                listAuditLogs(20, clientParam, ['UPLOAD']).catch(() => []),
            ]);
            setPickedUpFiles(pickedUp);
            setAuditLogs(logs);
            setLastRefresh(new Date());
        } catch (err) {
            console.error('Failed to load data:', err);
        } finally {
            setLoading(false);
        }
    }, [isAdmin, activeClientId, needsClientSelection]);

    // Auto-refresh every 60 seconds, and reload when client changes
    useEffect(() => {
        loadAllData();
        const interval = setInterval(loadAllData, 60000);
        return () => clearInterval(interval);
    }, [loadAllData]);

    const handleUpload = async (file: File) => {
        // Admin must select a client before uploading
        if (needsClientSelection) {
            setUploadError('Please select a client first');
            return;
        }

        // Must have doc type and partner selected
        if (!selectedDocType || !selectedPartner) {
            setUploadError('Please select document type and partner first');
            return;
        }

        setUploading(true);
        setUploadSuccess(null);
        setUploadError(null);

        try {
            // Generate timestamp for filename
            const now = new Date();
            const timestamp = now.toISOString().replace(/[-:]/g, '').replace('T', '-').slice(0, 15);

            // Rename file to {docType}_{partner}_{timestamp}_{originalFilename}
            const renamedFile = new File(
                [file],
                `${selectedDocType}_${selectedPartner}_${timestamp}_${file.name}`,
                { type: file.type }
            );

            // Pass clientId for admins who have selected a client
            const clientParam = isAdmin && activeClientId ? activeClientId : undefined;
            const result = await uploadFile(renamedFile, clientParam, selectedDocType, selectedPartner);
            setUploadSuccess(`Successfully uploaded: ${result.file.originalName}`);

            // Reset form after successful upload
            setSelectedDocType(null);
            setSelectedPartner(null);

            await loadAllData();
        } catch (err: any) {
            setUploadError(err.message || 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (file: FileInfo) => {
        setDownloading(file.path);
        try {
            const result = await getDownloadUrl(file.path);
            window.open(result.downloadUrl, '_blank');
        } catch (err: any) {
            console.error('Download failed:', err);
        } finally {
            setDownloading(null);
        }
    };

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragActive(false);

            // Don't handle drop if not ready
            if (!uploadReady) return;

            const droppedFiles = Array.from(e.dataTransfer.files);
            if (droppedFiles.length > 0) {
                handleUpload(droppedFiles[0]);
            }
        },
        [uploadReady]
    );

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (uploadReady) {
            setDragActive(true);
        }
    };

    const handleDragLeave = () => {
        setDragActive(false);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!uploadReady) {
            setUploadError('Please select document type and partner first');
            return;
        }
        const selectedFiles = e.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            handleUpload(selectedFiles[0]);
        }
        e.target.value = ''; // Reset input
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const formatTimeAgo = (dateStr: string): string => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f8f9fa', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <header
                style={{
                    background: 'linear-gradient(135deg, #1a2b4b 0%, #2d4a7c 100%)',
                    color: 'white',
                    padding: '16px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* Logo */}
                    <EDIPartnersLogo height={36} />
                    <div>
                        <Title1 style={{ color: 'white', margin: 0, fontSize: '20px', lineHeight: '1.2' }}>
                            Client Upload Portal
                        </Title1>
                        <Body1 style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                            Welcome, {user.displayName}
                        </Body1>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* Client selector for admins */}
                    {isAdmin && clients.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Building24Regular style={{ color: 'rgba(255,255,255,0.8)' }} />
                            <Dropdown
                                placeholder="Select a client..."
                                value={activeClientId ? (clients.find(c => c.clientId === activeClientId)?.displayName || activeClientId) : undefined}
                                selectedOptions={activeClientId ? [activeClientId] : []}
                                onOptionSelect={(_, data) => {
                                    if (data.optionValue && onClientChange) {
                                        onClientChange(data.optionValue as string);
                                    }
                                }}
                                style={{
                                    minWidth: '200px',
                                    background: 'rgba(255,255,255,0.15)',
                                    borderColor: 'rgba(255,255,255,0.3)',
                                }}
                            >
                                {clients.map(client => (
                                    <Option key={client.clientId} value={client.clientId} text={`${client.displayName} (${client.clientId.toUpperCase()})`}>
                                        {client.displayName} ({client.clientId.toUpperCase()})
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                    ) : isAdmin ? (
                        <Badge
                            appearance="filled"
                            style={{
                                background: '#ffc107',
                                color: '#000',
                                padding: '4px 12px',
                                borderRadius: '4px',
                            }}
                        >
                            No clients configured
                        </Badge>
                    ) : (
                        <Badge
                            appearance="filled"
                            style={{
                                background: '#00b4d8',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '4px',
                            }}
                        >
                            {user.clientId.toUpperCase()}
                        </Badge>
                    )}
                    {isAdmin && (
                        <Button
                            appearance="subtle"
                            icon={<Settings24Regular />}
                            onClick={() => navigate('/admin')}
                            style={{ color: 'white' }}
                        >
                            Admin
                        </Button>
                    )}
                    <Button
                        appearance="subtle"
                        icon={<SignOut24Regular />}
                        onClick={onLogout}
                        style={{ color: 'white' }}
                    >
                        Sign Out
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px', flex: 1, width: '100%' }}>
                {/* Admin client selection notice */}
                {isAdmin && clients.length === 0 && (
                    <MessageBar intent="warning" style={{ marginBottom: '24px', borderRadius: '8px' }}>
                        <MessageBarBody>
                            <strong>No clients configured.</strong> Go to{' '}
                            <Button appearance="transparent" onClick={() => navigate('/admin')} style={{ padding: '0 4px', minWidth: 'auto' }}>
                                Admin → Clients
                            </Button>{' '}
                            to create your first client before uploading files.
                        </MessageBarBody>
                    </MessageBar>
                )}
                {isAdmin && clients.length > 0 && !selectedClientId && (
                    <MessageBar intent="info" style={{ marginBottom: '24px', borderRadius: '8px' }}>
                        <MessageBarBody>
                            <strong>Select a client</strong> from the dropdown above to view files and upload.
                        </MessageBarBody>
                    </MessageBar>
                )}

                {/* Upload Card */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        padding: '24px',
                    }}
                >
                    <Title3 style={{ marginBottom: '8px', color: '#1a2b4b' }}>
                        Upload Files
                    </Title3>
                    <Body1 style={{ marginBottom: '20px', color: '#6c757d' }}>
                        Select document type and trading partner, then upload your file.
                        Files will be processed automatically and sent to your trading partners.
                    </Body1>

                    {uploadSuccess && (
                        <MessageBar intent="success" style={{ marginBottom: '16px', borderRadius: '6px' }}>
                            <MessageBarBody>
                                <Checkmark24Regular style={{ marginRight: '8px', color: '#28a745' }} />
                                {uploadSuccess}
                            </MessageBarBody>
                        </MessageBar>
                    )}

                    {uploadError && (
                        <MessageBar intent="error" style={{ marginBottom: '16px', borderRadius: '6px' }}>
                            <MessageBarBody>
                                <Dismiss24Regular style={{ marginRight: '8px' }} />
                                {uploadError}
                            </MessageBarBody>
                        </MessageBar>
                    )}

                    {/* Document Type and Partner Selection */}
                    <div style={{ display: 'flex', gap: '24px', marginBottom: '20px' }}>
                        <div style={{ flex: 1 }}>
                            <Label htmlFor="doc-type-dropdown" style={{ marginBottom: '6px', display: 'block', fontWeight: 600 }}>
                                <DocumentBulletList24Regular style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Document Type <span style={{ color: '#d13438' }}>*</span>
                            </Label>
                            <Dropdown
                                id="doc-type-dropdown"
                                placeholder="Select document type..."
                                value={selectedDocType ? (allowedDocTypes.find(d => d.value === selectedDocType)?.label ?? '') : ''}
                                selectedOptions={selectedDocType ? [selectedDocType] : []}
                                onOptionSelect={(_, data) => {
                                    setSelectedDocType(data.optionValue as string);
                                }}
                                style={{ width: '100%' }}
                            >
                                {allowedDocTypes.map(docType => (
                                    <Option key={docType.value} value={docType.value} text={docType.label}>
                                        {docType.label}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                        <div style={{ flex: 1 }}>
                            <Label htmlFor="partner-dropdown" style={{ marginBottom: '6px', display: 'block', fontWeight: 600 }}>
                                <Organization24Regular style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Trading Partner <span style={{ color: '#d13438' }}>*</span>
                            </Label>
                            <Dropdown
                                id="partner-dropdown"
                                placeholder="Select trading partner..."
                                value={selectedPartner ? (allowedPartners.find(p => p.value === selectedPartner)?.label ?? '') : ''}
                                selectedOptions={selectedPartner ? [selectedPartner] : []}
                                onOptionSelect={(_, data) => {
                                    setSelectedPartner(data.optionValue ? String(data.optionValue) : null);
                                }}
                                style={{ width: '100%' }}
                            >
                                {allowedPartners.map(partner => (
                                    <Option key={partner.value} value={partner.value} text={partner.label}>
                                        {partner.label}
                                    </Option>
                                ))}
                            </Dropdown>
                        </div>
                    </div>

                    {/* Selection Summary */}
                    {(selectedDocType || selectedPartner) && (
                        <div style={{
                            background: uploadReady ? '#e8f5e9' : '#fff3e0',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            {uploadReady ? (
                                <Checkmark24Regular style={{ color: '#28a745' }} />
                            ) : (
                                <Dismiss24Regular style={{ color: '#ff9800' }} />
                            )}
                            <Body2 style={{ color: uploadReady ? '#2e7d32' : '#e65100' }}>
                                {uploadReady
                                    ? `Ready to upload: Files will be named ${selectedDocType}_${selectedPartner}_YYYYMMDD-HHMMSS_filename.ext`
                                    : `Please select ${!selectedDocType ? 'document type' : ''}${!selectedDocType && !selectedPartner ? ' and ' : ''}${!selectedPartner ? 'trading partner' : ''}`
                                }
                            </Body2>
                        </div>
                    )}

                    {/* Drop Zone */}
                    <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        style={{
                            border: `2px dashed ${!uploadReady ? '#ccc' : dragActive ? '#00b4d8' : '#dee2e6'}`,
                            borderRadius: '12px',
                            padding: '48px',
                            textAlign: 'center',
                            background: !uploadReady ? '#f5f5f5' : dragActive ? 'rgba(0, 180, 216, 0.08)' : '#fafafa',
                            cursor: uploadReady ? 'pointer' : 'not-allowed',
                            transition: 'all 0.2s ease',
                            opacity: uploadReady ? 1 : 0.6,
                        }}
                        onClick={() => uploadReady && document.getElementById('file-input')?.click()}
                    >
                        <input
                            id="file-input"
                            type="file"
                            accept=".xlsx,.xls,.csv,.txt,.edi,.x12"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                            disabled={!uploadReady}
                        />

                        {uploading ? (
                            <div>
                                <Spinner size="large" style={{ color: '#00b4d8' }} />
                                <Body1 style={{ marginTop: '16px', color: '#1a2b4b' }}>
                                    Uploading...
                                </Body1>
                            </div>
                        ) : (
                            <div>
                                <div
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        margin: '0 auto 16px',
                                        background: uploadReady
                                            ? 'linear-gradient(135deg, #00b4d8 0%, #48cae4 100%)'
                                            : 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <ArrowUpload24Regular style={{ fontSize: '32px', color: 'white' }} />
                                </div>
                                <Title3 style={{ color: uploadReady ? '#1a2b4b' : '#9e9e9e', marginBottom: '12px', display: 'block' }}>
                                    {uploadReady ? 'Drop files here or click to browse' : 'Select document type and partner first'}
                                </Title3>
                                <Body1 style={{ color: '#6c757d', display: 'block', marginTop: '8px' }}>
                                    Supported formats: Excel, CSV, TXT, EDI, X12 (max 50MB)
                                </Body1>
                            </div>
                        )}
                    </div>
                </Card>

                {/* Recent Uploads */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Title3 style={{ color: '#1a2b4b' }}>File Status</Title3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Caption1 style={{ color: '#6c757d' }}>
                            Last updated: {lastRefresh.toLocaleTimeString()}
                        </Caption1>
                        <Button
                            appearance="subtle"
                            onClick={loadAllData}
                            disabled={loading}
                            style={{ color: '#00b4d8' }}
                        >
                            {loading ? <Spinner size="tiny" /> : 'Refresh'}
                        </Button>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
                    {/* Audit Log Column */}
                    <Card
                        style={{
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            padding: '16px',
                            height: '450px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <History24Regular style={{ color: '#6c757d' }} />
                            <Body1 style={{ fontWeight: 600, color: '#1a2b4b', fontSize: '16px' }}>Activity Log</Body1>
                        </div>
                        <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: '8px' }} />
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {auditLogs.length === 0 ? (
                                <Body2 style={{ color: '#6c757d', textAlign: 'center', padding: '40px 20px' }}>
                                    No activity yet
                                </Body2>
                            ) : (
                                auditLogs.map((log, index, arr) => (
                                    <div
                                        key={index}
                                        style={{
                                            padding: '8px 0',
                                            borderBottom: index < arr.length - 1 ? '1px solid #eee' : 'none',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Badge
                                                appearance="outline"
                                                style={{
                                                    fontSize: '10px',
                                                    color: log.action === 'UPLOAD' ? '#28a745' : log.action === 'DOWNLOAD' ? '#00b4d8' : '#6c757d',
                                                }}
                                            >
                                                {log.action}
                                            </Badge>
                                            <Caption1 style={{ color: '#999' }}>{formatTimeAgo(log.timestamp)}</Caption1>
                                        </div>
                                        <Body2 style={{ color: '#333', marginTop: '4px', fontSize: '12px', wordBreak: 'break-word' }}>
                                            {log.details}
                                        </Body2>
                                    </div>
                                ))
                            )}
                        </div>
                    </Card>

                    {/* Picked Up Column */}
                    <Card
                        data-testid="picked-up-files"
                        style={{
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                            padding: '16px',
                            height: '450px',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <CheckmarkCircle24Regular style={{ color: '#28a745' }} />
                            <Body1 style={{ fontWeight: 600, color: '#1a2b4b', fontSize: '16px' }}>Picked Up</Body1>
                            {pickedUpFiles.length > 0 && (
                                <Badge appearance="filled" style={{ background: '#28a745', color: '#fff' }}>
                                    {pickedUpFiles.length}
                                </Badge>
                            )}
                        </div>
                        <div style={{ borderBottom: '1px solid #e0e0e0', marginBottom: '8px' }} />
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                            {pickedUpFiles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6c757d' }}>
                                    <CheckmarkCircle24Regular style={{ fontSize: '32px', opacity: 0.3 }} />
                                    <Body2 style={{ marginTop: '8px' }}>No processed files</Body2>
                                    <Caption1 style={{ marginTop: '4px', display: 'block' }}>
                                        Processed files appear here
                                    </Caption1>
                                </div>
                            ) : (
                                pickedUpFiles.map((file) => (
                                    <div
                                        key={file.path}
                                        style={{
                                            padding: '10px',
                                            borderBottom: '1px solid #eee',
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            gap: '8px',
                                        }}
                                    >
                                        <Document24Regular style={{ color: '#28a745', flexShrink: 0, alignSelf: 'flex-start', marginTop: '2px' }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <Body2
                                                style={{
                                                    color: '#00b4d8',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                }}
                                                onClick={() => handleDownload(file)}
                                            >
                                                {downloading === file.path ? 'Getting link...' : file.name}
                                            </Body2>
                                            <Caption1 style={{ color: '#999', display: 'block' }}>
                                                {formatFileSize(file.size)}
                                            </Caption1>
                                            <Caption1 style={{ color: '#999', display: 'block' }}>
                                                {formatTimeAgo(file.lastModified)}
                                            </Caption1>
                                        </div>
                                        <Button
                                            appearance="subtle"
                                            icon={<ArrowDownload24Regular />}
                                            onClick={() => handleDownload(file)}
                                            disabled={downloading === file.path}
                                            style={{ color: '#00b4d8', flexShrink: 0 }}
                                            size="small"
                                        />
                                    </div>
                                ))
                            )}
                        </div>
                    </Card>
                </div>
            </main>

            {/* Footer */}
            <footer
                style={{
                    background: '#1a2b4b',
                    color: 'white',
                    padding: '24px',
                    textAlign: 'center',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '24px',
                        flexWrap: 'wrap',
                    }}
                >
                    <Body1 style={{ color: 'rgba(255,255,255,0.9)' }}>
                        © 2026 EDI Partners
                    </Body1>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>|</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Phone24Regular style={{ fontSize: '16px', color: '#00b4d8' }} />
                        <Body1 style={{ color: 'rgba(255,255,255,0.9)' }}>
                            804-755-4381
                        </Body1>
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.4)' }}>|</span>
                    <Body1 style={{ color: 'rgba(255,255,255,0.7)' }}>
                        <a href="mailto:EDISupport@EDI-Partners.com" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none' }}>
                            EDISupport@EDI-Partners.com
                        </a>
                    </Body1>
                </div>
            </footer>
        </div>
    );
}
